<template>
  <UiDialog :open="true" title="Preferences" @update:open="value => { if (!value) emit('close'); }">
    <form class="space-y-4" @submit.prevent="submit">
      <UiTabs class="preferences-tabs" v-model="activeTab" label="Preference sections" :options="preferenceTabs" />

      <!-- Section: Learning & Bot Settings -->
      <section v-if="activeTab === 'learning'" class="space-y-4" aria-label="Learning preferences">
        <div>
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500" for="transcription-pref">
            Transcription Format
          </label>
          <select
            id="transcription-pref"
            :value="selectedTranscription"
            class="ui-input w-full"
            @change="onTranscriptionChange"
          >
            <option
              v-for="t in availableTranscriptions"
              :key="t.code"
              :value="t.code"
            >
              {{ t.name }}
            </option>
          </select>
          <p class="mt-1 text-xs text-gray-400">
            Pronunciation guide displayed on flashcards (from bot settings).
          </p>
        </div>

        <div>
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500" for="translation-pref">
            Translation Language
          </label>
          <select
            id="translation-pref"
            :value="selectedTranslation"
            class="ui-input w-full"
            @change="onTranslationChange"
          >
            <option
              v-for="lang in availableLanguages"
              :key="lang.code"
              :value="lang.code"
            >
              {{ lang.name }}
            </option>
          </select>
          <p class="mt-1 text-xs text-gray-400">
            Target language for card definitions and translations.
          </p>
        </div>

        <div>
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500">
            Active Dictionaries / Decks
          </label>
          <div class="flex flex-wrap gap-2 pt-1">
            <button
              v-for="d in availableDictionaries"
              :key="d.id || d.code"
              type="button"
              class="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition select-none"
              :class="selectedDicts.includes(d.code)
                ? 'bg-blue-50 border-blue-400 font-semibold text-blue-900 dark:bg-blue-900/40 dark:text-blue-200'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'"
              @click="toggleDictionary(d.code)"
            >
              <i
                class="fa-solid"
                :class="selectedDicts.includes(d.code) ? 'fa-square-check text-blue-600 dark:text-blue-400' : 'fa-square text-gray-300'"
              ></i>
              <span>{{ getDictionaryDisplayName(d) }}</span>
            </button>
          </div>
        </div>

        <div class="space-y-3 pt-1 border-t border-gray-100 dark:border-gray-800">
          <div>
            <label class="mb-1 block text-xs font-bold uppercase text-gray-500" for="daily-goal-pref">Daily Study Goal</label>
            <select
              id="daily-goal-pref"
              :value="dailyGoal"
              class="ui-input w-full"
              @change="onDailyGoalChange"
            >
              <option :value="10">10 cards / day (Light)</option>
              <option :value="20">20 cards / day (Recommended)</option>
              <option :value="30">30 cards / day (Intermediate)</option>
              <option :value="50">50 cards / day (Intensive)</option>
            </select>
          </div>
        </div>

        <!-- Quick Access to Word Lists from Bot Info -->
        <div class="pt-2 border-t border-gray-100 dark:border-gray-800">
          <label class="mb-1 block text-xs font-bold uppercase text-gray-500">
            Word Lists Management
          </label>
          <p class="text-xs text-gray-400 mb-2">
            Inspect words in your current course directly on the learning canvas:
          </p>
          <div class="grid grid-cols-2 gap-2">
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs"
              @click="jumpToWordList('active')"
            >
              <i class="fa-solid fa-book-open mr-1.5 text-blue-500"></i>
              <span>Active Words</span>
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs"
              @click="jumpToWordList('learned')"
            >
              <i class="fa-solid fa-heart mr-1.5 text-rose-500"></i>
              <span>Learned Words</span>
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs"
              @click="jumpToWordList('postponed')"
            >
              <i class="fa-solid fa-clock mr-1.5 text-amber-500"></i>
              <span>Postponed Words</span>
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs"
              @click="jumpToWordList('part_of_speech')"
            >
              <i class="fa-solid fa-shapes mr-1.5 text-emerald-500"></i>
              <span>By Part of Speech</span>
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              type="button"
              class="justify-start text-xs col-span-2"
              @click="jumpToWordList('removed')"
            >
              <i class="fa-solid fa-trash mr-1.5 text-gray-500"></i>
              <span>Removed Words</span>
            </UiButton>
          </div>
        </div>
      </section>

      <section v-else-if="activeTab === 'appearance'" aria-label="Appearance preferences">
        <AppearanceControls />
      </section>

      <!-- Section: Telegram Connection -->
      <section v-else-if="activeTab === 'telegram'" class="space-y-4" aria-label="Telegram preferences">
        <div v-if="currentUser" class="p-4 rounded-xl border border-gray-200 bg-white/50 space-y-3">
          <div class="flex items-center gap-3">
            <UiAvatar :name="displayName" size="lg" />
            <div>
              <p class="font-bold text-sm text-gray-900">{{ displayName }}</p>
              <p v-if="currentUser.username" class="text-xs text-gray-500">@{{ currentUser.username }}</p>
              <p class="text-[11px] text-gray-400">Telegram ID: {{ currentUser.user_id || currentUser.id }}</p>
            </div>
          </div>
          <div class="flex items-center gap-2 pt-1">
            <span class="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
            <span class="text-xs text-emerald-700 font-medium">Native Telegram OAuth Active</span>
          </div>
          <div class="flex flex-wrap gap-2 pt-2">
            <UiButton variant="secondary" size="sm" type="button" @click="handleReLogin">
              <i class="fa-solid fa-arrows-rotate mr-1.5"></i>
              Switch / Re-login
            </UiButton>
            <UiButton
              variant="ghost"
              size="sm"
              type="button"
              class="text-blue-600 hover:text-blue-700"
              @click="openTelegramBot"
            >
              <i class="fa-brands fa-telegram mr-1.5"></i>
              Open Bot in Telegram
            </UiButton>
          </div>
        </div>
        <div v-else class="text-center p-6 rounded-xl border border-dashed space-y-3">
          <p class="text-sm text-gray-500">You are currently studying as a guest.</p>
          <UiButton variant="primary" size="md" shape="pill" type="button" @click="handleReLogin">
            <i class="fa-brands fa-telegram mr-1.5"></i>
            Login with Telegram
          </UiButton>
        </div>
      </section>

      <!-- Section: Storage & Cache (adapted from TaskManager) -->
      <section v-else class="space-y-4" aria-label="Storage preferences">
        <div>
          <h4 class="text-xs font-bold uppercase text-gray-500">Offline Storage & Media Cache</h4>
          <p class="mt-1 text-xs text-gray-400">
            Flashcard data and downloaded lesson media are cached for offline use.
          </p>
        </div>

        <div class="rounded-xl border border-gray-200 bg-white/50 p-4 space-y-2 text-xs">
          <div class="flex justify-between text-gray-700">
            <span>Offline App Shell:</span>
            <span class="font-medium text-emerald-600 dark:text-emerald-400" data-testid="offline-shell-status">
              {{ appShellPresaved ? 'Ready (Presaved)' : 'Active (Service Worker)' }}
            </span>
          </div>
          <div class="flex justify-between text-gray-700">
            <span>Offline Vocabulary Cached:</span>
            <span class="font-medium text-gray-900">{{ localSync.state.totalWords }} words</span>
          </div>
          <div class="flex justify-between text-gray-700">
            <span>Pending 3-Way Merge Changes:</span>
            <span class="font-medium text-gray-900">{{ localSync.state.pending }} changes</span>
          </div>
          <div class="flex justify-between text-gray-700">
            <span>Offline Sync Storage:</span>
            <span class="font-medium text-gray-900">Active (IndexedDB)</span>
          </div>
        </div>


        <div class="pt-2">
          <UiButton
            variant="ghost"
            type="button"
            class="user-menu-action text-xs text-amber-700 hover:text-amber-800"
            :disabled="isClearingCache"
            @click="cleanCacheAndRefresh"
          >
            <i class="fa-solid fa-rotate mr-1.5" :class="{ 'animate-spin': isClearingCache }"></i>
            <span>{{ isClearingCache ? 'Clearing cache...' : 'Clean Cache and Refresh Application' }}</span>
          </UiButton>
        </div>
      </section>

      <p v-if="error" class="text-sm text-red-500">{{ error }}</p>

      <div class="flex justify-end gap-2 pt-3 border-t border-[var(--ui-border)]">
        <UiButton variant="secondary" type="button" @click="emit('close')">{{ activeTab === 'appearance' ? 'Close' : 'Cancel' }}</UiButton>
        <UiButton
          v-if="activeTab !== 'appearance'"
          variant="primary"
          type="submit"
          :disabled="loading"
          class="disabled:opacity-50"
        >
          <span v-if="loading"><i class="fa-solid fa-circle-notch mr-1.5 animate-spin"></i></span>
          Save Preferences
        </UiButton>
      </div>
    </form>
  </UiDialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import {
  UiButton,
  UiDialog,
  UiTabs,
  UiAvatar,
} from '../ui';
import AppearanceControls from './AppearanceControls.vue';
import { useAuth } from '../composables/useAuth';
import { api } from '../services/api';
import { mediaCache } from '../services/mediaCacheService';
import { localSync } from '../services/LocalSyncService';


const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'filter-action', filterKind: string): void;
  (e: 'open-login'): void;
  (e: 'preferences-saved'): void;
}>();

type PreferenceTab = 'learning' | 'appearance' | 'telegram' | 'storage';
const activeTab = ref<PreferenceTab>('learning');

const preferenceTabs = [
  { value: 'learning', label: 'Learning' },
  { value: 'appearance', label: 'Appearance' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'storage', label: 'Storage' },
];

const { currentUser } = useAuth();
const displayName = computed(() => {
  return currentUser.value?.first_name || currentUser.value?.username || 'Guest';
});

// Learning preferences state
const selectedTranscription = ref('ipa');
const selectedTranslation = ref('en');
const selectedDicts = ref<string[]>(['es']);
const dailyGoal = ref(20);
const botUsername = ref('IgorLearnWordsDevBot');

const availableLanguages = ref([
  { code: 'en', name: '🇺🇸 English' },
  { code: 'ru', name: '🇷🇺 Russian' },
  { code: 'es', name: '🇪🇸 Spanish' },
  { code: 'de', name: '🇩🇪 German' },
  { code: 'fr', name: '🇫🇷 French' },
  { code: 'ka', name: '🇬🇪 Georgian' },
  { code: 'cat', name: 'Catalan' },
]);

const availableTranscriptions = ref([
  { code: 'ipa', name: '🗣️ IPA Broad (Standard)' },
  { code: 'ipa_narrow', name: '🗣️ IPA Narrow (Detailed)' },
  { code: 'lat', name: '🔤 Latin Transliteration' },
  { code: 'cyr', name: '🔤 Cyrillic Transliteration' },
  { code: '-', name: '🙅 No Transcription' },
]);

const availableDictionaries = ref<Array<{ id?: string; code: string; name: string; language?: string }>>([
  { id: 'es', code: 'es', name: 'Spanish Core' },
  { id: 'ka', code: 'ka', name: 'Georgian Basic' },
  { id: 'cat', code: 'cat', name: 'Catalan' },
]);

const getDictionaryDisplayName = (d: { id?: string; code: string; name: string; language?: string }) => {
  if (d.name && d.name !== 'Personal Dictionary') {
    return d.name;
  }
  if (d.language) {
    return d.language;
  }
  const lang = availableLanguages.value.find((l) => l.code === d.code);
  return lang ? lang.name : (d.code ? d.code.toUpperCase() : 'Personal Dictionary');
};

const onTranscriptionChange = (e: Event) => {
  selectedTranscription.value = (e.target as HTMLSelectElement).value;
};

const onTranslationChange = (e: Event) => {
  selectedTranslation.value = (e.target as HTMLSelectElement).value;
};

const onDailyGoalChange = (e: Event) => {
  dailyGoal.value = Number((e.target as HTMLSelectElement).value);
};

// Storage state
const appShellPresaved = ref(true);
const isClearingCache = ref(false);

const loading = ref(false);
const error = ref('');

const toggleDictionary = (code: string) => {
  if (selectedDicts.value.includes(code)) {
    if (selectedDicts.value.length > 1) {
      selectedDicts.value = selectedDicts.value.filter((c) => c !== code);
    }
  } else {
    selectedDicts.value.push(code);
  }
};

const jumpToWordList = (filterKind: string) => {
  emit('filter-action', filterKind);
  emit('close');
};

const handleReLogin = () => {
  emit('close');
  emit('open-login');
};

const openTelegramBot = () => {
  const username = botUsername.value || 'IgorLearnWordsDevBot';
  window.open(`https://t.me/${username}`, '_blank');
};

const cleanCacheAndRefresh = async () => {
  isClearingCache.value = true;
  try {
    await mediaCache.clearCache();
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch (err) {
    console.warn('Cache clear warning:', err);
  } finally {
    isClearingCache.value = false;
    window.location.reload();
  }
};

onMounted(async () => {
  try {
    // 1. Load offline IndexedDB preferences & catalogs first
    const [localPrefs, localCatalogs] = await Promise.all([
      localSync.getPreferences(),
      localSync.getCatalogs(),
    ]);
    if (localPrefs) {
      selectedTranscription.value = localPrefs.transcription || 'ipa';
      selectedTranslation.value = localPrefs.translation || 'en';
      if (localPrefs.selected_dictionaries?.length) {
        selectedDicts.value = [...localPrefs.selected_dictionaries];
      }

      dailyGoal.value = localPrefs.daily_goal || 20;
    }
    if (localCatalogs) {
      if (localCatalogs.bot_username) botUsername.value = localCatalogs.bot_username;
      if (localCatalogs.available_languages?.length) availableLanguages.value = localCatalogs.available_languages;
      if (localCatalogs.available_transcriptions?.length) availableTranscriptions.value = localCatalogs.available_transcriptions;
      if (localCatalogs.available_dictionaries?.length) availableDictionaries.value = localCatalogs.available_dictionaries;
    }

    // 2. If online, also refresh from server endpoint
    if (localSync.state.connection !== 'offline' && typeof navigator !== 'undefined' && navigator.onLine) {
      const prefs = await api.getUserPreferences();
      if (prefs) {
        selectedTranscription.value = prefs.transcription || selectedTranscription.value;
        selectedTranslation.value = prefs.translation || selectedTranslation.value;
        if (prefs.selected_dictionaries?.length) {
          selectedDicts.value = prefs.selected_dictionaries;
        }

        dailyGoal.value = prefs.daily_goal || dailyGoal.value;
        if ((prefs as any).bot_username) {
          botUsername.value = (prefs as any).bot_username;
        }
        if (prefs.available_languages?.length) {
          availableLanguages.value = prefs.available_languages;
        }
        if (prefs.available_transcriptions?.length) {
          availableTranscriptions.value = prefs.available_transcriptions;
        }
        if (prefs.available_dictionaries?.length) {
          availableDictionaries.value = prefs.available_dictionaries;
        }
      }
    }
  } catch {
    // Offline or guest: local IndexedDB preferences already applied
  }

  try {
    const presaved = await mediaCache.ensureAppShellPresaved();
    appShellPresaved.value = presaved || 'caches' in window;
  } catch {
    appShellPresaved.value = 'caches' in window;
  }
});

async function submit() {
  if (activeTab.value === 'appearance') { emit('close'); return; }
  error.value = '';
  loading.value = true;
  try {
    // Save learning preferences to IndexedDB & queue 3-way merge mutation
    await localSync.savePreferences({
      transcription: selectedTranscription.value,
      translation: selectedTranslation.value,
      selected_dictionaries: [...selectedDicts.value],
      last_language: selectedDicts.value[0] || 'ka',

      daily_goal: dailyGoal.value,
    });

    // 3. When online, also call direct preferences endpoint for immediate server/mock sync
    if (localSync.state.connection !== 'offline' && typeof navigator !== 'undefined' && navigator.onLine) {
      await api
        .updateUserPreferences({
          transcription: selectedTranscription.value,
          translation: selectedTranslation.value,
          selected_dictionaries: selectedDicts.value,

          daily_goal: dailyGoal.value,
        })
        .catch(() => null);
    }

    emit('preferences-saved');
    emit('close');
  } catch (err: any) {
    error.value = err?.message || 'Failed to save preferences';
  } finally {
    loading.value = false;
  }
}
</script>


<style scoped>
.appearance-wash-slider {
  accent-color: var(--ui-primary);
}
</style>
